import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useAuth } from "../../firebase";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase";
import { ORDER_ACTION_LABELS } from "../../constants/orderActions";
import { ORDER_ACTION_URLS } from "../../constants/orderSteps";

// Type for order data (simplified for dashboard)
interface DashboardOrderData {
  paypalOrderId: string;
  status: string;
  createdAt: any;
  actionsRequired: string[];
  items: {
    name: string;
    productId: string;
  }[];
}

const DashboardTab: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<DashboardOrderData[]>([]);
  const [recentActivity, setRecentActivity] = useState<DashboardOrderData[]>(
    []
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const userId = user ? user.uid : null;

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      setOrders([]);
      setRecentActivity([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Create query for user's orders that need action
    const ordersRef = collection(db, "orders");
    const activeOrdersQuery = query(
      ordersRef,
      where("userId", "==", userId),
      where("status", "in", ["pending", "awaiting-info", "in-progress"]),
      orderBy("createdAt", "desc"),
      limit(5)
    );

    // Create query for recent activity (all orders, limited)
    const recentActivityQuery = query(
      ordersRef,
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(3)
    );

    // Set up real-time listener for active orders
    const unsubscribeActive = onSnapshot(
      activeOrdersQuery,
      (querySnapshot) => {
        try {
          const activeOrders: DashboardOrderData[] = [];

          querySnapshot.forEach((doc) => {
            const data = doc.data();

            // Only include orders that actually have actions required
            if (
              Array.isArray(data.actionsRequired) &&
              data.actionsRequired.length > 0
            ) {
              const order: DashboardOrderData = {
                paypalOrderId: doc.id,
                status: data.status,
                createdAt: data.createdAt,
                actionsRequired: data.actionsRequired,
                items: Array.isArray(data.items) ? data.items : [],
              };
              activeOrders.push(order);
            }
          });

          setOrders(activeOrders);
          setError(null);
        } catch (err: any) {
          console.error("Error processing active orders:", err);
          setError("Failed to load active orders");
        }
      },
      (err) => {
        console.error("Error fetching active orders:", err);
        setError("Failed to load orders");
      }
    );

    // Set up real-time listener for recent activity
    const unsubscribeRecent = onSnapshot(
      recentActivityQuery,
      (querySnapshot) => {
        try {
          const recentOrders: DashboardOrderData[] = [];

          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const order: DashboardOrderData = {
              paypalOrderId: doc.id,
              status: data.status,
              createdAt: data.createdAt,
              actionsRequired: Array.isArray(data.actionsRequired)
                ? data.actionsRequired
                : [],
              items: Array.isArray(data.items) ? data.items : [],
            };
            recentOrders.push(order);
          });

          setRecentActivity(recentOrders);
          setIsLoading(false);
        } catch (err: any) {
          console.error("Error processing recent activity:", err);
          setIsLoading(false);
        }
      },
      (err) => {
        console.error("Error fetching recent activity:", err);
        setIsLoading(false);
      }
    );

    // Cleanup listeners on unmount
    return () => {
      unsubscribeActive();
      unsubscribeRecent();
    };
  }, [userId]);

  // Helper function to get action description
  const getActionDescription = (
    actionKey: string,
    productName: string
  ): string => {
    const label = ORDER_ACTION_LABELS[actionKey];
    if (label === "Upload Photos") {
      return `Upload photos for your ${productName}`;
    } else if (label === "Fill Out Family Tree") {
      return `Complete family tree information for your ${productName}`;
    } else if (label === "Fill Out Missionary Form") {
      return `Complete missionary information for your ${productName}`;
    } else if (label === "Fill Out Generational Form") {
      return `Complete generational art form for your ${productName}`;
    }
    return `Complete ${label} for your ${productName}`;
  };

  // Helper function to handle action button click
  const handleActionClick = (actionKey: string, orderId: string) => {
    const targetUrl = ORDER_ACTION_URLS[actionKey];
    if (targetUrl) {
      // Add order ID as query parameter for form pre-population
      const urlWithOrderId = `${targetUrl}?orderId=${orderId}`;
      navigate(urlWithOrderId);
    } else {
      console.warn(`No URL mapping found for action: ${actionKey}`);
    }
  };

  // Helper function to format dates
  const formatDate = (createdAt: any): string => {
    if (!createdAt) return "Unknown date";

    try {
      const date = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) return "1 day ago";
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 14) return "1 week ago";
      if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
      return date.toLocaleDateString();
    } catch (error) {
      return "Recently";
    }
  };

  // Helper function to get status description
  const getStatusDescription = (status: string): string => {
    switch (status) {
      case "pending":
        return "Order placed";
      case "awaiting-info":
        return "Waiting for information";
      case "in-progress":
        return "Being processed";
      case "completed":
        return "Order completed";
      case "canceled":
        return "Order canceled";
      default:
        return "Order updated";
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Actions Needed */}
      <Paper sx={{ padding: "16px", marginBottom: "24px" }}>
        <Typography variant="h6" gutterBottom>
          Actions Needed
        </Typography>
        {orders.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
            No actions needed at this time. All your orders are up to date!
          </Typography>
        ) : (
          <List>
            {orders.map((order) =>
              order.actionsRequired.map((actionKey, index) => {
                const productName = order.items[0]?.name || "order";
                const actionLabel = ORDER_ACTION_LABELS[actionKey] || actionKey;

                return (
                  <ListItem
                    key={`${order.paypalOrderId}-${actionKey}-${index}`}
                  >
                    <ListItemText
                      primary={getActionDescription(actionKey, productName)}
                      secondary={`Order #${order.paypalOrderId.toUpperCase()}`}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        handleActionClick(actionKey, order.paypalOrderId)
                      }
                    >
                      {actionLabel}
                    </Button>
                  </ListItem>
                );
              })
            )}
          </List>
        )}
      </Paper>

      {/* Recent Activity */}
      <Paper sx={{ padding: "16px", marginBottom: "24px" }}>
        <Typography variant="h6" gutterBottom>
          Recent Activity
        </Typography>
        {recentActivity.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
            No recent activity found.
          </Typography>
        ) : (
          <List>
            {recentActivity.map((order) => {
              const productName = order.items[0]?.name || "Unknown Product";
              return (
                <ListItem key={order.paypalOrderId}>
                  <ListItemText
                    primary={`${getStatusDescription(
                      order.status
                    )} - ${productName}`}
                    secondary={formatDate(order.createdAt)}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default DashboardTab;
